import { useEffect, useRef, useState, useCallback } from 'react';
import api from '../utils/api';
import BAMBU_ERRORS from '../utils/bambuErrors';
import {
  Plus, Pencil, Trash2, Wifi, WifiOff, Droplets, CircleDot, Clock,
  Monitor, RefreshCw, X, Timer, Layers, Thermometer, Fan, Gauge,
  SortAsc, Volume2, VolumeX, Pause, Play, Square, RotateCcw, Cctv,
} from 'lucide-react';
import LottieAnim from '../components/LottieAnim';
import PageHeader from '../components/PageHeader';

export function getErrorMsg(mqtt) {
  if (mqtt?.printError > 0) {
    return mqtt.printErrorMsg || BAMBU_ERRORS[mqtt.printError] || `오류 코드: 0x${mqtt.printError.toString(16).toUpperCase()}`;
  }
  // print_error가 없으면 HMS 에러에서 찾기
  if (mqtt?.hmsErrors?.length > 0) {
    const first = mqtt.hmsErrors.find(h => h.msg);
    if (first) return first.msg;
  }
  return null;
}

// ── 상수 ──
const STATUS_LABEL = { idle: '대기', printing: '출력 중', paused: '일시정지', error: '오류', offline: '오프라인' };
const STATUS_COLOR = {
  idle: 'bg-green-500/20 text-green-400 border-green-500/30',
  printing: 'bg-brand/20 text-brand border-brand/30',
  paused: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  error: 'bg-red-500/20 text-red-400 border-red-500/30',
  offline: 'bg-gray-700 text-gray-500 border-gray-700',
};
const GCODE_LABEL = { RUNNING: '출력 중', FINISH: '완료', PAUSE: '일시정지', FAILED: '실패', IDLE: '대기', PREPARE: '준비 중' };
const SORT_OPTIONS = [
  { value: 'remaining_asc', label: '남은시간 ↑' },
  { value: 'name', label: '이름순' },
  { value: 'filament_asc', label: '필라멘트 ↑' },
  { value: 'error_first', label: '오류 먼저' },
  { value: 'status', label: '상태순' },
];

// ── 유틸 ──
export function fmtMin(min) {
  if (min == null || min <= 0) return '--';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}h${String(m).padStart(2, '0')}m` : `${m}m`;
}
export function etaString(min) {
  if (min == null || min <= 0) return null;
  const eta = new Date(Date.now() + min * 60000);
  return `${String(eta.getHours()).padStart(2, '0')}:${String(eta.getMinutes()).padStart(2, '0')}`;
}
export function formatUsage(minutes) {
  if (!minutes) return '0h';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h${m > 0 ? ` ${m}m` : ''}` : `${m}m`;
}
// 팬 속도 (0-100%) 계산 + 클램프
// Bambu: cooling_fan_speed 0~15 스케일 (1스텝 = 약 6.67%)
// Klipper: 이미 0~100% 정수로 변환됨 (gateway klipper.js)
export function fanPercent(mqtt) {
  const raw = parseInt(mqtt?.coolingFan);
  if (!raw) return 0;
  const pct = mqtt?._source === 'klipper' ? raw : Math.round(raw / 15 * 100);
  return Math.max(0, Math.min(100, pct));
}
function playBeep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [0, 0.3, 0.6].forEach(delay => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = 'square'; osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.3, ctx.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.25);
      osc.start(ctx.currentTime + delay); osc.stop(ctx.currentTime + delay + 0.25);
    });
  } catch {}
}
function sortPrinters(printers, mqttMap, sortBy) {
  const SO = { error: 0, printing: 1, paused: 2, idle: 3, offline: 4 };
  return [...printers].sort((a, b) => {
    switch (sortBy) {
      case 'remaining_asc': {
        const aRun = mqttMap[a.id]?.gcodeState === 'RUNNING' ? 0 : 1;
        const bRun = mqttMap[b.id]?.gcodeState === 'RUNNING' ? 0 : 1;
        if (aRun !== bRun) return aRun - bRun;
        return (mqttMap[a.id]?.mcRemainingTime ?? Infinity) - (mqttMap[b.id]?.mcRemainingTime ?? Infinity);
      }
      case 'filament_asc':
        return (a.minFilamentGram ?? Infinity) - (b.minFilamentGram ?? Infinity);
      case 'error_first':
        return (a.status === 'error' ? 0 : 1) - (b.status === 'error' ? 0 : 1) || a.name.localeCompare(b.name);
      case 'status':
        return (SO[a.status] ?? 9) - (SO[b.status] ?? 9);
      default:
        return a.name.localeCompare(b.name);
    }
  });
}

// ── 카메라 스냅샷 폴링 (1초 간격) ──
// MJPEG long-lived 스트림 대신 짧은 JPEG 폴링 사용.
// 이유: 브라우저 HTTP/1.1 per-origin 6 연결 한도. 프린터 8대면 마지막 2개가 큐에 갇힘.
// 스냅샷 폴링은 요청이 짧게 끝나서 슬롯을 점유하지 않음 → 8대 모두 표시 가능.
function CameraFeed({ printerId, cameraStyle, className = '', pollInterval = 500, pollDelay = 0 }) {
  const token = localStorage.getItem('mfg_token');
  const [tick, setTick] = useState(() => Date.now());
  const [errCount, setErrCount] = useState(0);
  const hasNoCamera = cameraStyle === 'none';

  useEffect(() => {
    if (hasNoCamera) return;
    let iv;
    // pollDelay로 시작 시점 분산 → 동시 요청 방지
    const to = setTimeout(() => {
      setTick(Date.now());
      iv = setInterval(() => setTick(Date.now()), pollInterval);
    }, pollDelay);
    return () => { clearTimeout(to); clearInterval(iv); };
  }, [hasNoCamera, pollInterval, pollDelay]);

  // errCount >= 10 이면 15초 후 자동 리셋 (오프라인은 isOffline으로 차단, 일시적 실패는 빠르게 재시도)
  useEffect(() => {
    if (errCount < 10) return;
    const t = setTimeout(() => setErrCount(0), 15000);
    return () => clearTimeout(t);
  }, [errCount]);

  if (hasNoCamera) {
    return (
      <div className={`bg-gray-800 flex items-center justify-center ${className}`}>
        <div className="text-center">
          <Monitor size={20} className="text-gray-600 mx-auto mb-1" />
          <p className="text-gray-600 text-[10px]">카메라 없음</p>
        </div>
      </div>
    );
  }
  // 연속 10회 이상 실패 시 "재연결 중..." 표시 (8초 후 자동 재시도)
  if (errCount >= 10) {
    return (
      <div className={`bg-gray-800 flex items-center justify-center ${className}`}>
        <div className="text-center">
          <Monitor size={20} className="text-gray-600 mx-auto mb-1" />
          <p className="text-gray-600 text-[10px]">재연결 중...</p>
        </div>
      </div>
    );
  }
  const snapUrl = `/api/printers/${printerId}/camera/snapshot?token=${token}&t=${tick}`;
  return (
    <img
      src={snapUrl}
      alt="cam"
      className={`object-cover bg-gray-800 ${className}`}
      onLoad={() => { if (errCount > 0) setErrCount(0); }}
      onError={() => setErrCount(c => c + 1)}
    />
  );
}

// ── 프린터 등록/수정 모달 ──
function PrinterModal({ printer, gateways, onSave, onClose }) {
  const [form, setForm] = useState({
    name: printer?.name || '', type: printer?.type || 'bambu',
    gatewayId: printer?.gatewayId || '',
    serialNumber: printer?.serialNumber || '', accessCode: printer?.accessCode || '',
    localIp: printer?.localIp || '', moonrakerPort: printer?.moonrakerPort || 7125,
    cameraStyle: printer?.cameraStyle || 'mainsail',
    model: printer?.model || '', notes: printer?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isKlipper = form.type === 'klipper';
  const handleSave = async () => {
    // 클라이언트 측 기본 검증
    if (!form.name?.trim()) { setError('프린터 이름을 입력하세요.'); return; }
    if (!form.localIp?.trim()) { setError('로컬 IP를 입력하세요.'); return; }
    if (!isKlipper) {
      if (!form.serialNumber?.trim()) { setError('Bambu 시리얼 번호를 입력하세요.'); return; }
      if (!form.accessCode?.trim()) { setError('Access Code(PIN)를 입력하세요.'); return; }
    }

    setError('');
    setSaving(true);
    try {
      const payload = { ...form };
      // Klipper 는 serialNumber / accessCode 사용 안 함 → 빈 문자열 보내지 않음 (서버 normalize 에 의존하지 않고 명시적 제거)
      if (isKlipper) {
        payload.serialNumber = null;
        payload.accessCode = null;
        payload.model = payload.model || 'Klipper Printer';
      } else {
        payload.moonrakerPort = null;
        payload.model = payload.model || 'Bambu Lab';
      }
      if (printer?.id) await api.put(`/printers/${printer.id}`, payload);
      else await api.post('/printers', payload);
      onSave();
    } catch (e) {
      const msg = e.response?.data?.message || e.message || '저장 실패';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h2 className="text-white font-bold mb-4">{printer?.id ? '프린터 수정' : '프린터 등록'}</h2>
        <div className="space-y-3">
          {/* 타입 선택 */}
          <div>
            <label className="text-gray-400 text-xs mb-1 block">프린터 타입</label>
            <div className="flex gap-2">
              {[
                { value: 'bambu', label: 'Bambu Lab', sub: 'H2D / P1S / X1C' },
                { value: 'klipper', label: 'Klipper', sub: 'Flex4 / Moonraker' },
              ].map(t => (
                <button key={t.value} onClick={() => setForm(f => ({ ...f, type: t.value }))}
                  className={`flex-1 py-2.5 rounded-lg text-sm border transition-colors ${
                    form.type === t.value
                      ? 'bg-brand/20 border-brand/50 text-brand'
                      : 'bg-gray-800 border-gray-700 text-gray-500 hover:text-gray-300'
                  }`}>
                  <div className="font-medium">{t.label}</div>
                  <div className="text-xs opacity-60">{t.sub}</div>
                </button>
              ))}
            </div>
          </div>
          {/* 공통 필드 */}
          <div>
            <label className="text-gray-400 text-xs mb-1 block">프린터 이름</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="예: 프린터-01"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
          </div>
          <div>
            <label className="text-gray-400 text-xs mb-1 block">모델명</label>
            <input value={form.model} onChange={e => setForm(f => ({ ...f, model: e.target.value }))}
              placeholder={isKlipper ? '예: Lugoware Flex4' : '예: Bambu Lab P1S'}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
          </div>
          <div>
            <label className="text-gray-400 text-xs mb-1 block">로컬 IP</label>
            <input value={form.localIp} onChange={e => setForm(f => ({ ...f, localIp: e.target.value }))}
              placeholder="172.30.1.xxx"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
          </div>
          {/* Bambu 전용 */}
          {!isKlipper && (
            <>
              <div>
                <label className="text-gray-400 text-xs mb-1 block">시리얼 번호 (S/N)</label>
                <input value={form.serialNumber} onChange={e => setForm(f => ({ ...f, serialNumber: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
              </div>
              <div>
                <label className="text-gray-400 text-xs mb-1 block">Access Code (PIN)</label>
                <input value={form.accessCode} onChange={e => setForm(f => ({ ...f, accessCode: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
              </div>
            </>
          )}
          {/* Klipper 전용 */}
          {isKlipper && (
            <>
              <div>
                <label className="text-gray-400 text-xs mb-1 block">Moonraker 포트</label>
                <input type="number" value={form.moonrakerPort} onChange={e => setForm(f => ({ ...f, moonrakerPort: parseInt(e.target.value) || 7125 }))}
                  placeholder="7125"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
                <p className="text-gray-600 text-xs mt-1">Moonraker 기본 포트: 7125</p>
              </div>
              <div>
                <label className="text-gray-400 text-xs mb-1 block">카메라 프로토콜</label>
                <select value={form.cameraStyle} onChange={e => setForm(f => ({ ...f, cameraStyle: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand">
                  <option value="mainsail">Mainsail / Fluidd (기본) — nginx /webcam/</option>
                  <option value="snapmaker-u1">Snapmaker U1 — Moonraker 빌트인 카메라</option>
                  <option value="none">카메라 없음</option>
                </select>
                <p className="text-gray-600 text-xs mt-1">Lugoware·원더메이커는 Mainsail, 스냅메이커 U1은 전용 옵션</p>
              </div>
            </>
          )}
          {/* 게이트웨이 */}
          <div>
            <label className="text-gray-400 text-xs mb-1 block">게이트웨이</label>
            <select value={form.gatewayId || ''} onChange={e => setForm(f => ({ ...f, gatewayId: e.target.value }))}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand">
              <option value="">선택 안함</option>
              {gateways.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-gray-400 text-xs mb-1 block">메모</label>
            <textarea value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={2} className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand resize-none" />
          </div>
        </div>
        {error && (
          <div className="mt-4 bg-red-900/30 border border-red-700/50 text-red-300 text-xs rounded-lg px-3 py-2">
            {error}
          </div>
        )}
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} disabled={saving} className="flex-1 bg-gray-800 hover:bg-gray-700 text-white rounded-lg py-2 text-sm disabled:opacity-40">취소</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 bg-brand hover:bg-brand-600 text-white rounded-lg py-2 text-sm font-medium disabled:opacity-40">{saving ? '저장 중...' : '저장'}</button>
        </div>
      </div>
    </div>
  );
}

// ── 필라멘트 장착 모달 ──
export function FilamentModal({ printer, side, slot, stocks, onSave, onClose }) {
  const [selectedStockId, setSelectedStockId] = useState(slot?.stockId || '');
  const [remainingWeightGram, setRemainingWeightGram] = useState(slot?.remainingWeightGram || 1000);
  const [tab, setTab] = useState('new');
  const selectedStock = stocks.find(s => s.id === +selectedStockId);
  const newStocks = stocks.filter(s => s.remainingWeightGram >= s.totalWeightGram * s.spoolCount);
  const usedStocks = stocks.filter(s => s.remainingWeightGram < s.totalWeightGram * s.spoolCount);
  const filteredStocks = tab === 'new' ? newStocks : usedStocks;

  const handleSelect = (stockId) => {
    setSelectedStockId(stockId);
    const stock = stocks.find(s => s.id === +stockId);
    if (stock) {
      const isNew = stock.remainingWeightGram >= stock.totalWeightGram * stock.spoolCount;
      setRemainingWeightGram(isNew ? stock.totalWeightGram : stock.remainingWeightGram);
    }
  };
  const handleSave = async () => {
    if (!selectedStock) return;
    await api.put(`/filament/slots/printer/${printer.id}`, {
      [side]: { stockId: selectedStock.id, brand: selectedStock.brand, material: selectedStock.material, colorName: selectedStock.colorName, colorHex: selectedStock.colorHex, totalWeightGram: selectedStock.totalWeightGram, remainingWeightGram },
    });
    onSave();
  };
  const handleClear = async () => {
    await api.put(`/filament/slots/printer/${printer.id}`, {
      [side]: { brand: '', material: '', colorName: '', colorHex: '#ffffff', totalWeightGram: 1000, remainingWeightGram: 0, isEmpty: true, stockId: null },
    });
    onSave();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <h2 className="text-white font-bold mb-1">{printer.name} — {side === 'left' ? 'L' : 'R'} 필라멘트</h2>
        <p className="text-gray-500 text-sm mb-4">재고에서 선택</p>
        <div className="space-y-3">
          <div className="flex gap-1 bg-gray-800 rounded-lg p-1">
            {[{ value: 'new', label: `새 (${newStocks.length})` }, { value: 'used', label: `잔여 (${usedStocks.length})` }].map(t => (
              <button key={t.value} onClick={() => { setTab(t.value); setSelectedStockId(''); }}
                className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === t.value ? 'bg-brand text-white' : 'text-gray-400 hover:text-white'}`}>{t.label}</button>
            ))}
          </div>
          <div className="max-h-60 overflow-y-auto space-y-1.5">
            {filteredStocks.length === 0 && <p className="text-gray-600 text-sm text-center py-4">없음</p>}
            {filteredStocks.map(s => (
              <button key={s.id} onClick={() => handleSelect(String(s.id))}
                className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${+selectedStockId === s.id ? 'bg-brand/20 border border-brand/40' : 'bg-gray-800 border border-gray-700 hover:border-gray-600'}`}>
                <span className="w-4 h-4 rounded-full flex-shrink-0 border border-gray-600" style={{ backgroundColor: s.colorHex || '#888' }} />
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium">{s.material}</p>
                  <p className="text-gray-500 text-xs">{s.colorName} · {s.brand}</p>
                </div>
                <span className="text-gray-400 text-xs flex-shrink-0">{s.totalWeightGram}g × {s.spoolCount}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 bg-gray-800 hover:bg-gray-700 text-white rounded-lg py-2 text-sm">취소</button>
          {slot && <button onClick={handleClear} className="bg-gray-800 hover:bg-red-900/30 text-red-400 rounded-lg px-3 py-2 text-sm">비우기</button>}
          <button onClick={handleSave} disabled={!selectedStock} className="flex-1 bg-brand hover:bg-brand-600 text-white rounded-lg py-2 text-sm font-medium disabled:opacity-40">장착</button>
        </div>
      </div>
    </div>
  );
}

// ── 노즐 장착 모달 ──
export function NozzleSelectModal({ printer, side, nozzles, onSave, onClose }) {
  const currentNozzle = printer.nozzles?.find(n => n.slotNo === (side === 'left' ? 0 : 1));
  const [selectedId, setSelectedId] = useState('');
  const available = nozzles.filter(n => !n.printerId);

  const handleMount = async () => {
    if (!selectedId) return;
    await api.put(`/nozzles/printer/${printer.id}`, { [side]: { nozzleId: +selectedId } });
    onSave();
  };
  const handleUnmount = async () => {
    await api.put(`/nozzles/printer/${printer.id}`, { [side]: { unmount: true } });
    onSave();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <h2 className="text-white font-bold mb-1">{printer.name} — {side === 'left' ? 'L' : 'R'} 노즐</h2>
        <p className="text-gray-500 text-sm mb-4">노즐 재고에서 선택</p>
        <div className="max-h-60 overflow-y-auto space-y-1.5">
          {available.length === 0 && <p className="text-gray-600 text-sm text-center py-4">사용 가능한 노즐이 없습니다.</p>}
          {available.map(n => (
            <button key={n.id} onClick={() => setSelectedId(String(n.id))}
              className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${+selectedId === n.id ? 'bg-brand/20 border border-brand/40' : 'bg-gray-800 border border-gray-700 hover:border-gray-600'}`}>
              <CircleDot size={14} className="text-gray-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium">{n.material} {n.diameter}mm</p>
                <p className="text-gray-500 text-xs">{formatUsage(n.usageMinutes)} 사용</p>
              </div>
            </button>
          ))}
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 bg-gray-800 hover:bg-gray-700 text-white rounded-lg py-2 text-sm">취소</button>
          {currentNozzle && <button onClick={handleUnmount} className="bg-gray-800 hover:bg-red-900/30 text-red-400 rounded-lg px-3 py-2 text-sm">해제</button>}
          <button onClick={handleMount} disabled={!selectedId} className="flex-1 bg-brand hover:bg-brand-600 text-white rounded-lg py-2 text-sm font-medium disabled:opacity-40">장착</button>
        </div>
      </div>
    </div>
  );
}

// ── 상세 모달 (클릭 시 확대) ──
export function DetailModal({ printer, mqtt, stocks, nozzles, onEdit, onFilament, onNozzle, onClose, onControl }) {
  const { filamentSlots = [] } = printer;
  const isPrinting = mqtt?.gcodeState === 'RUNNING';
  const isPaused = mqtt?.gcodeState === 'PAUSE';
  const isFinished = mqtt?.gcodeState === 'FINISH';
  const isIdle = !isPrinting && !isPaused && printer.status !== 'offline';
  const canControl = isPrinting || isPaused;
  const eta = isPrinting ? etaString(mqtt?.mcRemainingTime) : null;

  // 재출력 기능
  const [reprintJobs, setReprintJobs] = useState([]);
  const [showReprint, setShowReprint] = useState(false);
  const [reprintLoading, setReprintLoading] = useState(false);
  const [reprintTab, setReprintTab] = useState('this'); // 'this' | 'all'


  const loadReprintJobs = async (tab = 'this') => {
    try {
      const url = tab === 'this'
        ? `/jobs/reprintable/list?printerId=${printer.id}`
        : '/jobs/reprintable/list';
      const { data } = await api.get(url);
      setReprintJobs(Array.isArray(data) ? data : []);
      setReprintTab(tab);
      setShowReprint(true);
    } catch (e) {
      console.error('재출력 목록 조회 실패:', e);
      setReprintJobs([]);
      setReprintTab(tab);
      setShowReprint(true);
    }
  };

  const switchReprintTab = async (tab) => {
    if (tab === reprintTab) return;
    try {
      const url = tab === 'this'
        ? `/jobs/reprintable/list?printerId=${printer.id}`
        : '/jobs/reprintable/list';
      const { data } = await api.get(url);
      setReprintJobs(Array.isArray(data) ? data : []);
      setReprintTab(tab);
    } catch (e) {
      console.error('재출력 목록 조회 실패:', e);
      setReprintJobs([]);
      setReprintTab(tab);
    }
  };

  const handleReprint = async (jobId) => {
    if (!confirm('이 출력물을 이 프린터에서 재출력하시겠습니까?')) return;
    setReprintLoading(true);
    try {
      await api.post(`/jobs/${jobId}/reprint`, { printerId: printer.id });
      setShowReprint(false);
      alert('재출력이 시작되었습니다.');
    } catch (e) {
      alert(e.response?.data?.message || '재출력 실패');
    } finally {
      setReprintLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/95 z-50" onClick={onClose}>
      <div className="h-full w-full max-w-6xl mx-auto p-2 sm:p-4 overflow-y-auto flex flex-col md:overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* 헤더 */}
        <div className="flex items-center justify-between mb-2 sm:mb-3 flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${printer.status === 'error' ? 'bg-red-500 animate-ping' : isPrinting ? 'bg-brand animate-pulse' : 'bg-gray-500'}`} />
            <h2 className="text-white font-bold text-base sm:text-xl truncate">{printer.name}</h2>
            <span className={`text-xs px-2 py-0.5 rounded-full border flex-shrink-0 ${STATUS_COLOR[printer.status] || STATUS_COLOR.offline}`}>
              {STATUS_LABEL[printer.status]}
            </span>
            {mqtt?.gcodeState && mqtt.gcodeState !== 'IDLE' && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0
                ${isPrinting ? 'bg-blue-500/30 text-blue-300 border border-blue-500/30'
                  : isFinished ? 'bg-emerald-500/30 text-emerald-300 border border-emerald-500/30'
                  : 'bg-gray-700/60 text-gray-300 border border-gray-600/30'}`}>
                {GCODE_LABEL[mqtt.gcodeState] || mqtt.gcodeState}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* 프린터 제어 버튼 */}
            {canControl && (
              <div className="flex items-center gap-1 mr-2 border-r border-gray-700 pr-2">
                {isPrinting && (
                  <button onClick={() => onControl(printer.id, 'pause')} className="flex items-center gap-1 px-2.5 py-1.5 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 text-xs font-medium">
                    <Pause size={14} /> 일시정지
                  </button>
                )}
                {isPaused && (
                  <button onClick={() => onControl(printer.id, 'resume')} className="flex items-center gap-1 px-2.5 py-1.5 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 text-xs font-medium">
                    <Play size={14} /> 재개
                  </button>
                )}
                <button onClick={() => onControl(printer.id, 'stop')} className="flex items-center gap-1 px-2.5 py-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 text-xs font-medium">
                  <Square size={14} /> 중지
                </button>
              </div>
            )}
            <button onClick={(e) => { e.stopPropagation(); onEdit(printer); }} className="p-2 text-gray-500 hover:text-white rounded-lg hover:bg-gray-800">
              <Pencil size={16} />
            </button>
            <button onClick={onClose} className="p-2 text-gray-500 hover:text-white rounded-lg hover:bg-gray-800">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* 카메라 */}
        <div className="w-full aspect-video md:flex-1 md:min-h-0 mb-2 sm:mb-3 flex-shrink-0">
          <CameraFeed printerId={printer.id} cameraStyle={printer.cameraStyle} className="w-full h-full rounded-xl object-contain" />
        </div>

        {/* 에러 정보 */}
        {(getErrorMsg(mqtt) || mqtt?.gcodeState === 'FAILED') && (
          <div className="flex-shrink-0 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 mb-2">
            <p className="text-red-400 text-sm font-medium">{getErrorMsg(mqtt) || '출력 실패'}</p>
          </div>
        )}

        {/* 하단 정보 바 */}
        <div className="flex-shrink-0 bg-gray-900 rounded-xl p-3 sm:p-4 overflow-x-auto">
          {/* 진행률 */}
          {mqtt && (isPrinting || isFinished) && (
            <div className="mb-3">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-white font-bold">{mqtt.mcPercent ?? 0}%</span>
                {isPrinting && <span className="text-gray-400">{fmtMin(mqtt.mcRemainingTime)} 남음{eta && ` (~${eta})`}</span>}
                {isFinished && <span className="text-emerald-400">출력 완료</span>}
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div className={`h-full rounded-full transition-all ${isFinished ? 'bg-emerald-500' : 'bg-brand'}`} style={{ width: `${mqtt.mcPercent ?? 0}%` }} />
              </div>
            </div>
          )}


          {/* MQTT 정보 */}
          {mqtt && (
            <div className="flex items-center gap-4 flex-wrap text-sm mb-3">
              {[{ l: '노즐', v: mqtt.nozzleTemper != null ? Math.round(mqtt.nozzleTemper) : null, t: mqtt.nozzleTarget != null ? Math.round(mqtt.nozzleTarget) : null }, { l: '베드', v: mqtt.bedTemper != null ? Math.round(mqtt.bedTemper) : null, t: mqtt.bedTarget != null ? Math.round(mqtt.bedTarget) : null }]
                .filter(x => x.v != null).map(x => (
                  <div key={x.l} className="flex items-center gap-1">
                    <Thermometer size={14} className={x.v > 50 ? 'text-orange-400' : 'text-gray-500'} />
                    <span className="text-gray-400">{x.l}</span>
                    <span className={`font-mono font-bold ${x.v > 50 ? 'text-orange-400' : 'text-white'}`}>{x.v}°</span>
                    {x.t > 0 && <span className="text-gray-600 font-mono text-xs">→{x.t}°</span>}
                  </div>
                ))}
              {mqtt.layerNum != null && (
                <div className="flex items-center gap-1"><Layers size={14} className="text-gray-500" /><span className="text-white font-mono">{mqtt.layerNum}<span className="text-gray-500">/{mqtt.totalLayerNum}</span></span></div>
              )}
              <div className="flex items-center gap-1"><Gauge size={14} className="text-gray-500" /><span className="text-white font-mono">{mqtt.spdMag ?? 100}%</span></div>
              {parseInt(mqtt.coolingFan) > 0 && (
                <div className="flex items-center gap-1"><Fan size={14} className="text-blue-400" /><span className="text-white font-mono">{fanPercent(mqtt)}%</span></div>
              )}
              {mqtt.wifiSignal && (
                <div className="flex items-center gap-1"><Wifi size={14} className="text-gray-500" /><span className="text-gray-400">{mqtt.wifiSignal}</span></div>
              )}
            </div>
          )}

          {/* 필라멘트 + 노즐 — 4칸 균등 그리드 */}
          <div className="grid grid-cols-4 gap-2">
            {[0, 1].map(slotNo => {
              const slot = filamentSlots.find(s => s.slotNo === slotNo);
              const low = slot && slot.remainingWeightGram <= 150;
              const pct = slot ? Math.round((slot.remainingWeightGram / slot.totalWeightGram) * 100) : 0;
              const side = slotNo === 0 ? 'left' : 'right';
              return (
                <button key={`f${slotNo}`} onClick={() => onFilament(printer, side, slot)}
                  className="bg-gray-800 rounded-lg p-3 hover:bg-gray-750 transition-colors text-left">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Droplets size={10} className="text-gray-600" />
                    <span className="text-gray-500 text-xs font-bold">{slotNo === 0 ? 'L' : 'R'}</span>
                    {slot?.colorHex && <span className="w-2.5 h-2.5 rounded-full border border-gray-600 ml-auto" style={{ backgroundColor: slot.colorHex }} />}
                  </div>
                  {slot ? (
                    <>
                      <p className="text-white text-xs font-medium truncate mb-1">{slot.colorName || slot.material}</p>
                      <div className="w-full bg-gray-700 rounded-full h-1 mb-1">
                        <div className={`h-1 rounded-full ${low ? 'bg-yellow-400' : 'bg-brand'}`} style={{ width: `${Math.max(3, pct)}%` }} />
                      </div>
                      <span className={`text-xs font-mono ${low ? 'text-yellow-400' : 'text-gray-400'}`}>{slot.remainingWeightGram}g</span>
                    </>
                  ) : <p className="text-gray-600 text-xs">없음</p>}
                </button>
              );
            })}
            {[0, 1].map(slotNo => {
              const nozzle = printer.nozzles?.find(n => n.slotNo === slotNo);
              const side = slotNo === 0 ? 'left' : 'right';
              return (
                <button key={`n${slotNo}`} onClick={() => onNozzle(printer, side)}
                  className="bg-gray-800 rounded-lg p-3 hover:bg-gray-750 transition-colors text-left">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <CircleDot size={10} className="text-gray-600" />
                    <span className="text-gray-500 text-xs font-bold">{slotNo === 0 ? 'L' : 'R'}</span>
                  </div>
                  {nozzle ? (
                    <>
                      <p className="text-white text-xs font-medium truncate mb-1">{nozzle.material} {nozzle.diameter}mm</p>
                      <p className="text-gray-400 text-xs flex items-center gap-1"><Clock size={10} />{formatUsage(nozzle.usageMinutes)}</p>
                    </>
                  ) : <p className="text-gray-600 text-xs">없음</p>}
                </button>
              );
            })}
          </div>

          {/* 출력 이력 버튼 (대기 중인 프린터에서만) */}
          {isIdle && !showReprint && (
            <button onClick={() => loadReprintJobs('this')}
              className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 bg-gray-800 hover:bg-gray-750 rounded-lg text-sm text-gray-300 hover:text-white transition-colors">
              <RotateCcw size={14} /> 출력 이력 보기
            </button>
          )}

          {/* 출력 이력 목록 */}
          {showReprint && (
            <div className="mt-3 bg-gray-800 rounded-lg p-3 max-h-72 overflow-y-auto">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white text-xs font-bold">출력 이력</span>
                <button onClick={() => setShowReprint(false)} className="text-gray-500 hover:text-white"><X size={14} /></button>
              </div>
              {/* 탭: 이 프린터 / 전체 */}
              <div className="flex gap-1 mb-2">
                <button
                  onClick={() => switchReprintTab('this')}
                  className={`flex-1 py-1.5 text-xs rounded-lg transition-colors ${
                    reprintTab === 'this' ? 'bg-brand text-white' : 'bg-gray-700 text-gray-400 hover:text-white'
                  }`}
                >
                  이 프린터 이력
                </button>
                <button
                  onClick={() => switchReprintTab('all')}
                  className={`flex-1 py-1.5 text-xs rounded-lg transition-colors ${
                    reprintTab === 'all' ? 'bg-brand text-white' : 'bg-gray-700 text-gray-400 hover:text-white'
                  }`}
                >
                  전체 이력
                </button>
              </div>
              {reprintJobs.length === 0 ? (
                <p className="text-gray-500 text-xs py-4 text-center">
                  {reprintTab === 'this' ? '이 프린터의 출력 이력이 없습니다.' : '출력 이력이 없습니다.'}
                </p>
              ) : reprintJobs.map(job => {
                const isOtherPrinter = reprintTab === 'all' && job.printerId && job.printerId !== printer.id;
                const canReprint = job.canReprint;
                return (
                  <button
                    key={job.id}
                    onClick={() => canReprint && handleReprint(job.id)}
                    disabled={reprintLoading || !canReprint}
                    title={canReprint ? '재출력' : '3MF 파일이 없어 재출력 불가 (Bambu Studio/패널 직접 출력 이력)'}
                    className={`w-full flex items-center justify-between py-2 px-2 rounded-lg text-left group transition-colors ${
                      canReprint ? 'hover:bg-gray-700 cursor-pointer' : 'opacity-60 cursor-not-allowed'
                    }`}>
                    <div className="min-w-0">
                      <p className="text-white text-xs font-medium truncate">{job.productName || `잡 #${job.id}`}</p>
                      <p className="text-gray-500 text-[10px]">
                        <span className={isOtherPrinter ? 'text-yellow-400' : ''}>
                          {job.printer?.name || '프린터 미지정'}
                        </span>
                        {' · '}
                        {new Date(job.createdAt).toLocaleDateString('ko-KR')}
                        {job.recipientName && ` · ${job.recipientName}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      {isOtherPrinter && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400">
                          크로스
                        </span>
                      )}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                        job.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                        job.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                        'bg-gray-700 text-gray-400'
                      }`}>{job.status === 'failed' ? '실패' : job.status === 'completed' ? '완료' : '취소'}</span>
                      {canReprint
                        ? <Play size={12} className="text-gray-600 group-hover:text-brand" />
                        : <span className="text-[9px] text-gray-600">재출력 불가</span>
                      }
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── 필라멘트 슬롯 시각화 ──
function FilamentSlotView({ label, slot, onEdit }) {
  const isEmpty = !slot || slot.isEmpty || (!slot.material && !slot.brand && !slot.colorName);
  const pct = !isEmpty ? Math.round((slot.remainingWeightGram / slot.totalWeightGram) * 100) : 0;
  const low = !isEmpty && slot.remainingWeightGram <= 150;
  return (
    <button onClick={onEdit}
      className={`flex-1 rounded-xl p-3 text-left transition-colors hover:opacity-90 ${
        !isEmpty ? 'bg-gray-800 hover:bg-gray-750' : 'bg-gray-800/50 border border-dashed border-gray-700 hover:border-gray-600'
      }`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-gray-500">{label}</span>
        {!isEmpty && slot?.colorHex && <span className="w-3 h-3 rounded-full flex-shrink-0 border border-gray-600" style={{ backgroundColor: slot.colorHex }} />}
      </div>
      {!isEmpty ? (
        <>
          <p className="text-white text-xs font-medium truncate">{slot.colorName || slot.material || '이름 없음'}</p>
          <p className="text-gray-500 text-xs mb-2">{slot.material} · {slot.brand}</p>
          <div className="w-full bg-gray-700 rounded-full h-1.5 mb-1">
            <div className={`h-1.5 rounded-full ${low ? 'bg-yellow-400' : 'bg-brand'}`} style={{ width: `${Math.max(3, pct)}%` }} />
          </div>
          <p className={`text-xs ${low ? 'text-yellow-400 font-bold' : 'text-gray-400'}`}>{slot.remainingWeightGram}g {low && '⚠ 교체 필요'}</p>
        </>
      ) : (
        <>
          <p className="text-gray-600 text-xs font-medium">미장착</p>
          <p className="text-gray-600 text-xs mb-2">&nbsp;</p>
          <div className="w-full bg-gray-700/30 rounded-full h-1.5 mb-1" />
          <p className="text-gray-600 text-xs">클릭하여 등록</p>
        </>
      )}
    </button>
  );
}

// ── 프린터 카드 ──
function PrinterCard({ printer, mqtt, onClick, onEdit, onDelete, onFilament, onNozzle, hideCamera }) {
  const isOffline = printer.status === 'offline';
  const isPrinting = mqtt?.gcodeState === 'RUNNING';
  const isFinished = mqtt?.gcodeState === 'FINISH';
  const isError = printer.status === 'error';
  const STATUS_DOT = { idle: 'bg-green-400', printing: 'bg-brand', paused: 'bg-yellow-400', error: 'bg-red-400', offline: 'bg-gray-600' };
  const leftSlot = printer.filamentSlots?.find(s => s.slotNo === 0);
  const rightSlot = printer.filamentSlots?.find(s => s.slotNo === 1);
  const leftNozzle = printer.nozzles?.find(n => n.slotNo === 0);
  const rightNozzle = printer.nozzles?.find(n => n.slotNo === 1);

  // 카메라 + 카드 본체 공통 콘텐츠
  const cardContent = (
    <>
      {/* 카메라 영역 */}
      <div className="relative cursor-pointer" onClick={() => onClick(printer)}>
        {hideCamera ? (
          <div className="w-full aspect-video bg-gray-800" />
        ) : (
          <CameraFeed printerId={printer.id} cameraStyle={isOffline ? 'none' : printer.cameraStyle} className="w-full aspect-video" />
        )}
        {(getErrorMsg(mqtt) || mqtt?.gcodeState === 'FAILED') && (
          <div className="absolute top-0 left-0 right-0 bg-red-900/80 backdrop-blur-sm px-2 py-1.5">
            <p className="text-red-200 text-[10px] font-medium truncate">{getErrorMsg(mqtt) || '출력 실패'}</p>
          </div>
        )}
        {mqtt?.gcodeState && mqtt.gcodeState !== 'IDLE' && !(getErrorMsg(mqtt) || mqtt?.gcodeState === 'FAILED') && (
          <div className="absolute top-1.5 left-1.5">
            <span className={`text-[10px] px-1.5 py-px rounded-full backdrop-blur-sm font-medium
              ${isPrinting ? 'bg-blue-500/30 text-blue-300 border border-blue-500/30'
                : isFinished ? 'bg-emerald-500/30 text-emerald-300 border border-emerald-500/30'
                : 'bg-gray-700/60 text-gray-300 border border-gray-600/30'}`}>
              {GCODE_LABEL[mqtt.gcodeState] || mqtt.gcodeState}
            </span>
          </div>
        )}
        {isPrinting && mqtt?.mcPercent != null && (
          <div className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-sm px-2 py-1">
            <div className="flex items-center justify-between text-[10px] mb-0.5">
              <span className="text-white font-bold">{mqtt.mcPercent}%</span>
              <span className="flex items-center gap-1 text-gray-300"><Timer size={8} />{fmtMin(mqtt.mcRemainingTime)}</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-0.5">
              <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${mqtt.mcPercent}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* 카드 본체 */}
      <div className={`p-4 ${isPrinting ? 'bg-blue-950/50' : ''}`}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${STATUS_DOT[printer.status] || 'bg-gray-600'}`} />
            <div>
              <div className="flex items-center gap-1.5">
                <h2 className="text-white font-semibold text-sm">{printer.name}</h2>
                {printer.type === 'klipper' && <span className="text-[9px] px-1 py-px rounded bg-purple-500/20 text-purple-400">K</span>}
              </div>
              <p className="text-gray-600 text-xs">{printer.model}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${STATUS_COLOR[printer.status] || STATUS_COLOR.offline}`}>
              {STATUS_LABEL[printer.status] || printer.status}
            </span>
            <button onClick={() => onEdit(printer)} className="p-1.5 text-gray-600 hover:text-white rounded-lg hover:bg-gray-800">
              <Pencil size={12} />
            </button>
            <button onClick={() => onDelete(printer.id)} className="p-1.5 text-gray-600 hover:text-red-400 rounded-lg hover:bg-gray-800">
              <Trash2 size={12} />
            </button>
          </div>
        </div>
        {isPrinting && mqtt && (
          <div className="bg-gray-800/50 rounded-lg px-2.5 py-2 mb-3">
            {mqtt.gcodeFile && <p className="text-brand text-xs truncate font-medium mb-1">{mqtt.gcodeFile.split('/').pop().replace('.gcode', '')}</p>}
            <div className="flex items-center gap-2 text-xs text-gray-400">
              {mqtt.layerNum != null && <span className="flex items-center gap-0.5"><Layers size={10} className="text-gray-500" />{mqtt.layerNum}/{mqtt.totalLayerNum}</span>}
              <span className="flex items-center gap-0.5"><Thermometer size={10} className={mqtt.nozzleTemper > 50 ? 'text-orange-400' : 'text-gray-500'} />{mqtt.nozzleTemper != null ? Math.round(mqtt.nozzleTemper) : '--'}°</span>
              {mqtt._source !== 'klipper' && <span className="flex items-center gap-0.5"><Gauge size={10} className="text-gray-500" />{mqtt.spdMag ?? 100}%</span>}
              {parseInt(mqtt.coolingFan) > 0 && <span className="flex items-center gap-0.5"><Fan size={10} className="text-blue-400" />{fanPercent(mqtt)}%</span>}
            </div>
          </div>
        )}
        {!isPrinting && mqtt && !isOffline && (
          <div className="flex items-center gap-3 text-xs mb-3">
            <span className="flex items-center gap-0.5">
              <Thermometer size={10} className={mqtt.nozzleTemper > 50 ? 'text-orange-400' : 'text-gray-500'} />
              <span className={`font-mono ${mqtt.nozzleTemper > 50 ? 'text-orange-400' : 'text-gray-300'}`}>{mqtt.nozzleTemper != null ? Math.round(mqtt.nozzleTemper) : '--'}°</span>
            </span>
            <span className={`font-mono ${mqtt.bedTemper > 40 ? 'text-orange-300' : 'text-gray-300'}`}>{mqtt.bedTemper != null ? Math.round(mqtt.bedTemper) : '--'}°</span>
            {mqtt.wifiSignal && <span className="flex items-center gap-0.5 text-gray-500"><Wifi size={10} />{mqtt.wifiSignal}</span>}
          </div>
        )}
        <div className="grid grid-cols-2 gap-1 mb-3 text-xs">
          <div className="flex justify-between bg-gray-800/50 rounded px-2 py-1 whitespace-nowrap overflow-hidden">
            <span className="text-gray-500 flex-shrink-0">IP</span>
            <span className="text-gray-300 font-mono truncate ml-1">{printer.localIp || '-'}</span>
          </div>
          <div className="flex justify-between bg-gray-800/50 rounded px-2 py-1 whitespace-nowrap overflow-hidden">
            <span className="text-gray-500 flex-shrink-0">게이트웨이</span>
            <span className={`flex items-center gap-1 truncate ml-1 ${printer.gateway?.status === 'online' ? 'text-green-400' : 'text-gray-500'}`}>
              {printer.gateway?.status === 'online' ? <Wifi size={10} className="flex-shrink-0" /> : <WifiOff size={10} className="flex-shrink-0" />}
              <span className="truncate">{printer.gateway?.name || '미연결'}</span>
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1 mb-2">
          <Droplets size={12} className="text-gray-600" />
          <span className="text-gray-500 text-xs">필라멘트</span>
        </div>
        <div className="flex gap-2 mb-3">
          <FilamentSlotView label="L" slot={leftSlot} onEdit={() => onFilament(printer, 'left', leftSlot)} />
          <FilamentSlotView label="R" slot={rightSlot} onEdit={() => onFilament(printer, 'right', rightSlot)} />
        </div>
        <div className="flex items-center gap-1 mb-2">
          <CircleDot size={12} className="text-gray-600" />
          <span className="text-gray-500 text-xs">노즐</span>
        </div>
        <div className="flex gap-2">
          {[['L', 'left', leftNozzle], ['R', 'right', rightNozzle]].map(([label, side, nozzle]) => (
            <button key={label} onClick={() => onNozzle(printer, side)}
              className={`flex-1 rounded-xl p-3 text-left transition-colors hover:opacity-90 ${
                nozzle ? 'bg-gray-800 hover:bg-gray-750' : 'bg-gray-800/50 border border-dashed border-gray-700 hover:border-gray-600'
              }`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold text-gray-500">{label}</span>
                {nozzle && <Clock size={10} className="text-gray-600" />}
              </div>
              {nozzle ? (
                <>
                  <p className="text-white text-xs font-medium truncate">{nozzle.material} {nozzle.diameter}mm</p>
                  <p className="text-gray-400 text-xs">{formatUsage(nozzle.usageMinutes)}</p>
                </>
              ) : (
                <>
                  <p className="text-gray-600 text-xs font-medium">미장착</p>
                  <p className="text-gray-600 text-xs">클릭하여 장착</p>
                </>
              )}
            </button>
          ))}
        </div>
      </div>
    </>
  );

  // 출력중: 회전 그라디언트 테두리 래퍼 + 내부 다크 배경 레이어
  if (isPrinting) {
    return (
      <div className="printing-card">
        <div className="printing-card-inner">{cardContent}</div>
      </div>
    );
  }

  return (
    <div className={`bg-gray-900 border rounded-xl overflow-hidden ${isError ? 'border-red-500' : 'border-gray-800'}`}>
      {cardContent}
    </div>
  );
}

// ── 관제 모드 (전체화면 CCTV 바둑판) ──
function CctvView({ printers, mqttMap, onClose }) {
  const sorted = sortPrinters(printers, mqttMap, 'status');
  const count = sorted.length;
  // 바둑판 열 수: 1~2대→1, 3~4대→2, 5~9대→3, 10대+→4
  const cols = count <= 2 ? 1 : count <= 4 ? 2 : count <= 9 ? 3 : 4;
  const rows = Math.ceil(count / cols);

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      {/* 상단 바 */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-950 border-b border-gray-800 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Cctv size={18} className="text-brand" />
          <span className="text-white font-bold text-sm">관제 모드</span>
          <span className="text-gray-500 text-xs">{count}대</span>
        </div>
        <button onClick={onClose} className="p-1.5 text-gray-500 hover:text-white rounded-lg hover:bg-gray-800">
          <X size={18} />
        </button>
      </div>

      {/* 바둑판 그리드 — rows 명시해야 셀 높이가 0으로 collapse 안 됨 */}
      <div
        className="flex-1 overflow-hidden printer-grid-gaps"
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridTemplateRows: `repeat(${rows}, 1fr)`,
          gap: '1px',
        }}
      >
        {sorted.map((p, i) => {
          const mqtt = mqttMap[p.id];
          const isPrinting = mqtt?.gcodeState === 'RUNNING';
          const isError = p.status === 'error';
          const isOffline = p.status === 'offline';
          // 프린터마다 폴링 시작 시점을 분산 (1500ms 구간에 균등 배치)
          // 예: 6대면 0, 250, 500, 750, 1000, 1250ms 후 시작 → 동시 요청 없음
          const staggerDelay = sorted.length > 1 ? Math.floor(i * 1500 / sorted.length) : 0;

          return (
            <div key={p.id} className="relative bg-black overflow-hidden">
              {/* 카메라 — absolute inset-0으로 셀 꽉 채움, 1.5초 간격 폴링 */}
              <CameraFeed
                printerId={p.id}
                cameraStyle={isOffline ? 'none' : p.cameraStyle}
                className="absolute inset-0 w-full h-full object-cover"
                pollInterval={1500}
                pollDelay={staggerDelay}
              />

              {/* 진행바 (하단) */}
              {isPrinting && mqtt?.mcPercent != null && (
                <div className="absolute bottom-0 left-0 right-0">
                  <div className="w-full bg-black/40 h-0.5">
                    <div className="h-full bg-brand transition-all" style={{ width: `${mqtt.mcPercent}%` }} />
                  </div>
                </div>
              )}

              {/* 오버레이 (이름 + 상태) */}
              <div className="absolute bottom-1 left-1.5 right-1.5 flex items-end justify-between pointer-events-none">
                <div className="flex flex-col gap-0.5">
                  <span className="text-white text-[10px] font-bold leading-none drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]">
                    {p.name}
                  </span>
                  {isPrinting && (
                    <span className="text-[9px] text-brand font-medium leading-none">
                      {mqtt.mcPercent}% · {fmtMin(mqtt.mcRemainingTime)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {isError && (
                    <span className="text-[9px] px-1 py-px rounded bg-red-500/80 text-white">오류</span>
                  )}
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                    isError ? 'bg-red-400 animate-ping' :
                    isPrinting ? 'bg-brand animate-pulse' :
                    isOffline ? 'bg-gray-600' : 'bg-green-400'
                  }`} />
                </div>
              </div>

              {/* 에러 오버레이 */}
              {isError && getErrorMsg(mqtt) && (
                <div className="absolute top-0 left-0 right-0 bg-red-900/70 px-1.5 py-1">
                  <p className="text-red-200 text-[9px] truncate">{getErrorMsg(mqtt)}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── 메인 ──
export default function PrintersPage() {
  const [printers, setPrinters] = useState([]);
  const [gateways, setGateways] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [nozzles, setNozzles] = useState([]);
  const [mqttMap, setMqttMap] = useState({});
  const [sortBy, setSortBy] = useState('remaining_asc');
  const [soundOn, setSoundOn] = useState(true);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [cctvMode, setCctvMode] = useState(false);
  const [printerModal, setPrinterModal] = useState(null);
  const [filamentModal, setFilamentModal] = useState(null);
  const [nozzleModal, setNozzleModal] = useState(null);
  const prevErrorIds = useRef(new Set());

  const loadPrinters = useCallback(async () => {
    try {
      const r = await api.get('/printers/monitor/all');
      setPrinters(r.data);
      if (soundOn) {
        const errIds = new Set(r.data.filter(p => p.status === 'error').map(p => p.id));
        const ne = [...errIds].filter(id => !prevErrorIds.current.has(id));
        if (ne.length > 0) playBeep();
        prevErrorIds.current = errIds;
      }
    } finally { setLoading(false); }
  }, [soundOn]);

  const loadMqtt = useCallback(async () => {
    try {
      const r = await api.get('/printers/monitor/status');
      const m = {};
      r.data.forEach(s => { m[s.printerId] = s; });
      setMqttMap(m);
    } catch {}
  }, []);

  const loadExtras = () => {
    api.get('/gateways').then(r => setGateways(r.data));
    api.get('/filament/stock').then(r => setStocks(r.data));
    api.get('/nozzles').then(r => setNozzles(r.data));
  };

  useEffect(() => {
    loadPrinters();
    loadMqtt();
    loadExtras();
    const t1 = setInterval(loadPrinters, 15000);
    const t2 = setInterval(loadMqtt, 10000);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, [loadPrinters, loadMqtt]);

  useEffect(() => {
    if (selected) {
      const u = printers.find(p => p.id === selected.id);
      if (u) setSelected(u);
    }
  }, [printers]);

  const reload = () => { loadPrinters(); loadExtras(); };
  const del = async id => {
    if (!confirm('삭제하시겠습니까?')) return;
    await api.delete(`/printers/${id}`);
    reload();
  };
  const handleFilament = (printer, side, slot) => setFilamentModal({ printer, side, slot });
  const handleNozzle = (printer, side) => setNozzleModal({ printer, side });

  const errorCount = printers.filter(p => p.status === 'error').length;
  const printing = printers.filter(p => mqttMap[p.id]?.gcodeState === 'RUNNING');
  const sortedAll = sortPrinters(printers, mqttMap, sortBy);

  return (
    <div>
      {/* 헤더 */}
      <PageHeader icon={Monitor} title="프린터 관리" count={printers.length}>
        <button onClick={() => setSoundOn(s => !s)}
          className={`p-2 rounded-lg ${soundOn ? 'text-brand bg-brand/10' : 'text-gray-600'}`}>
          {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
        </button>
        <div className="hidden sm:flex items-center bg-gray-900 border border-gray-800 rounded-lg">
          <SortAsc size={14} className="text-gray-500 ml-2" />
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            className="bg-transparent text-white text-sm py-1.5 pr-2 pl-1 focus:outline-none">
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <button onClick={() => { loadPrinters(); loadMqtt(); }}
          className="p-2 text-gray-500 hover:text-white rounded-lg hover:bg-gray-800">
          <RefreshCw size={16} />
        </button>
        <button onClick={() => setCctvMode(true)}
          className="flex items-center gap-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 hover:text-white rounded-lg px-2.5 py-2 text-sm font-medium">
          <Cctv size={16} /><span className="hidden sm:inline">관제</span>
        </button>
        <button onClick={() => setPrinterModal({})}
          className="flex items-center gap-1.5 bg-brand hover:bg-brand-600 text-white rounded-lg px-2.5 py-2 text-sm font-medium">
          <Plus size={16} /><span className="hidden sm:inline">등록</span>
        </button>
      </PageHeader>

      {loading ? (
        <div className="flex flex-col items-center justify-center h-64">
          <LottieAnim type="loading" size={100} />
          <p className="text-gray-600 text-sm mt-2">프린터 정보 불러오는 중...</p>
        </div>
      ) : printers.length === 0 ? (
        <div className="text-center py-16">
          <div className="flex justify-center">
            <LottieAnim type="empty" size={120} />
          </div>
          <p className="text-gray-500 mb-2">등록된 프린터가 없습니다.</p>
          <button onClick={() => setPrinterModal({})} className="text-brand text-sm hover:underline">첫 번째 프린터 등록하기</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {sortedAll.map(p => (
            <PrinterCard key={p.id} printer={p} mqtt={mqttMap[p.id]}
              onClick={setSelected} onEdit={setPrinterModal} onDelete={del}
              onFilament={handleFilament} onNozzle={handleNozzle} hideCamera={selected?.id === p.id} />
          ))}
        </div>
      )}

      {/* 관제 모드 */}
      {cctvMode && (
        <CctvView printers={printers} mqttMap={mqttMap} onClose={() => setCctvMode(false)} />
      )}

      {/* 상세 모달 */}
      {selected && (
        <DetailModal printer={selected} mqtt={mqttMap[selected.id]}
          stocks={stocks} nozzles={nozzles}
          onEdit={(p) => { setSelected(null); setPrinterModal(p); }}
          onFilament={(p, side, slot) => { setSelected(null); handleFilament(p, side, slot); }}
          onNozzle={(p, side) => { setSelected(null); handleNozzle(p, side); }}
          onControl={async (printerId, command) => {
            if (!confirm(`프린터에 "${command === 'pause' ? '일시정지' : command === 'resume' ? '재개' : '중지'}" 명령을 보내시겠습니까?`)) return;
            try {
              await api.post(`/printers/${printerId}/control`, { command });
            } catch (e) {
              alert(e.response?.data?.message || '명령 실패');
            }
          }}
          onClose={() => setSelected(null)} />
      )}

      {/* 프린터 수정 모달 */}
      {printerModal !== null && (
        <PrinterModal printer={printerModal?.id ? printerModal : null} gateways={gateways}
          onSave={() => { reload(); setPrinterModal(null); }}
          onClose={() => setPrinterModal(null)} />
      )}

      {/* 필라멘트 모달 */}
      {filamentModal && (
        <FilamentModal printer={filamentModal.printer} side={filamentModal.side} slot={filamentModal.slot} stocks={stocks}
          onSave={() => { reload(); setFilamentModal(null); }}
          onClose={() => setFilamentModal(null)} />
      )}

      {/* 노즐 모달 */}
      {nozzleModal && (
        <NozzleSelectModal printer={nozzleModal.printer} side={nozzleModal.side} nozzles={nozzles}
          onSave={() => { reload(); setNozzleModal(null); }}
          onClose={() => setNozzleModal(null)} />
      )}
    </div>
  );
}
